import type React from "react";
import {
  composeRenderProps,
  ToggleButton,
  ToggleButtonGroup,
  type ToggleButtonGroupProps,
  type ToggleButtonProps,
} from "react-aria-components";
import toggleButtonStyles from "./ToggleButton.module.scss";
import toggleButtonGroupStyles from "./ToggleButtonGroup.module.scss";

type SingleSelectToggleButtonGroupProps<T extends string> = StrictOmit<
  ToggleButtonGroupProps,
  "selectedKeys" | "onSelectionChange"
> & {
  selectionMode: "single";
  disallowEmptySelection: true;
  selectedKey: T;
  onSelectionChange: (value: T) => void;
  items: Array<{
    id: T;
    content: React.ReactNode;
    "aria-label"?: string;
  }>;
};

const UnstyledSingleSelectToggleButton = <T extends string>({
  id,
  ...props
}: StrictOmit<ToggleButtonProps, "id"> & {
  id: T;
}) => {
  return <ToggleButton id={id} {...props} />;
};

/**
 * @description A single select toggle button group. Wraps the ToggleButtonGroup component and provides a single select behavior with robust type safety.
 * @param selectedKey - The key of the selected item.
 * @param onSelectionChange - The function to call when the selection changes.
 * @param items - The items to display in the button group.
 * @param props - The props to pass to the ToggleButtonGroup component. See https://react-aria.adobe.com/ToggleButtonGroup for more details.
 * @returns
 */
export const UnstyledSingleSelectToggleButtonGroup = <T extends string>({
  selectedKey,
  onSelectionChange,
  items,
  ...props
}: SingleSelectToggleButtonGroupProps<T>) => {
  return (
    <ToggleButtonGroup
      {...props}
      selectedKeys={[selectedKey]}
      onSelectionChange={(keys) => {
        const value = keys.values().next().value;
        if (value !== undefined) {
          onSelectionChange(value as T);
        }
      }}
    >
      {items.map((item) => (
        <UnstyledSingleSelectToggleButton
          key={item.id}
          id={item.id}
          aria-label={item["aria-label"]}
        >
          {item.content}
        </UnstyledSingleSelectToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const StyledSingleSelectToggleButton = <T extends string>({
  id,
  ...props
}: StrictOmit<ToggleButtonProps, "id"> & {
  id: T;
}) => {
  return (
    <ToggleButton
      id={id}
      {...props}
      className={composeRenderProps(props.className, (className) =>
        [toggleButtonStyles.toggleButton, className].filter(Boolean).join(" "),
      )}
    />
  );
};

export const SingleSelectToggleButtonGroup = <T extends string>({
  selectedKey,
  onSelectionChange,
  items,
  ...props
}: SingleSelectToggleButtonGroupProps<T>) => {
  return (
    <ToggleButtonGroup
      {...props}
      className={toggleButtonGroupStyles.toggleButtonGroup}
      selectedKeys={[selectedKey]}
      onSelectionChange={(keys) => {
        const value = keys.values().next().value;
        if (value !== undefined) {
          onSelectionChange(value as T);
        }
      }}
    >
      {items.map((item) => (
        <StyledSingleSelectToggleButton
          key={item.id}
          id={item.id}
          aria-label={item["aria-label"]}
        >
          {item.content}
        </StyledSingleSelectToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
